import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useGoalCreateMutation } from "web_myplann/Api/Goals/useGoalCreateMutation";
import { mapGoalFormToCreateRequest } from "web_myplann/Goals/Mappers/mapGoalFormToCreateRequest";
import { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { z } from "zod";

export const RENT_REAL_YIELD = 0.04;

export type RentDrawdownMode = "to-zero" | "perpetual";

const getGoalFormSchema = (t: (key: string) => string) =>
	z.object({
		category: z.nativeEnum(GoalCategoryTypeEnum, {
			required_error: t(Resources.Form.Validations.required),
		}),
		name: z
			.string({
				required_error: t(Resources.Form.Validations.required),
			})
			.min(1, t(Resources.Form.Validations.required)),
		dueDate: z.date({
			required_error: t(Resources.Form.Validations.required),
			invalid_type_error: t(Resources.Form.Validations.required),
		}),
		totalValue: z
			.number({
				required_error: t(Resources.Form.Validations.required),
				invalid_type_error: t(Resources.Form.Validations.required),
			})
			.positive(),
		ownFunds: z.number().min(0).optional(),
		loanAmount: z.number().min(0).optional(),
		monthlyRent: z.number().min(0).optional(),
		rentYears: z.number().min(0).optional(),
		drawdownMode: z.enum(["to-zero", "perpetual"]).optional(),
		statePension: z.number().min(0).optional(),
	});

export type GoalFormModel = z.infer<ReturnType<typeof getGoalFormSchema>>;

type UseGoalFormProps = {
	initialCategory?: GoalCategoryTypeEnum;
	onSuccess?: (goalId?: number) => void;
};

const getDefaultDueDate = () => {
	const now = new Date();
	return new Date(now.getFullYear() + 5, now.getMonth(), 1);
};

export const useGoalForm = ({
	initialCategory,
	onSuccess,
}: UseGoalFormProps) => {
	const { t } = useResource();
	const { mutate: createGoalMutation, isPending } = useGoalCreateMutation();

	const form = useForm<GoalFormModel>({
		resolver: zodResolver(getGoalFormSchema(t)),
		defaultValues: {
			category: initialCategory,
			name: "",
			dueDate: getDefaultDueDate(),
			ownFunds: 0,
			loanAmount: 0,
			drawdownMode: "to-zero",
			statePension: 0,
		},
	});

	const submit = form.handleSubmit((data) => {
		createGoalMutation(mapGoalFormToCreateRequest(data), {
			onSuccess: (response) => onSuccess?.(response.id ?? undefined),
		});
	});

	return {
		...form,
		submit,
		isSubmitting: isPending,
	};
};
